import { environment } from "../../config/environment";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useReactTable, flexRender, getCoreRowModel, getSortedRowModel, createColumnHelper } from "@tanstack/react-table";
import { formatTimeTo12Hour, formatDateMMDDYYYY } from "./utils";
import { customFetch } from "../../utils/Auth";

const PatientSummaryTable = ({ patients, onPatientIdClick, selectedPatientId, paginated, showDeleteColumn = true, onDeletionChange }) => {
  const columnHelper = createColumnHelper();
  const [localDeletionStatus, setLocalDeletionStatus] = useState({});
  const [updatingPatientId, setUpdatingPatientId] = useState(null);
  const data = useMemo(() => Array.isArray(patients) ? patients : patients ? [patients] : [], [patients]);

  useEffect(() => {
    if (!patients) {
      setLocalDeletionStatus({});
      return;
    }
    if (Array.isArray(patients)) {
      const statusMap = {};
      patients.forEach(patient => {
        if (patient && typeof patient.patient_id !== "undefined") {
          statusMap[patient.patient_id] = Boolean(patient.is_deleted);
        }
      });
      setLocalDeletionStatus(statusMap);
      return;
    }
    if (patients.patient_id) {
      setLocalDeletionStatus({ [patients.patient_id]: Boolean(patients.is_deleted) });
    }
  }, [patients]);

  const handleToggleDelete = useCallback(async (patient) => {
    if (!patient?.patient_id) return;
    const currentStatus = localDeletionStatus[patient.patient_id] ?? Boolean(patient.is_deleted);
    const nextValue = !currentStatus;

    setUpdatingPatientId(patient.patient_id);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/update-field`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patient_id: patient.patient_id,
            field: "is_deleted",
            value: nextValue,
          }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to update patient deletion status");
      }

      setLocalDeletionStatus(prev => ({
        ...prev,
        [patient.patient_id]: nextValue,
      }));
      onDeletionChange?.(patient.patient_id, nextValue);
    } catch (error) {
      console.error("Failed to toggle patient deletion:", error);
      alert("Failed to update patient deletion status. Please try again.");
    } finally {
      setUpdatingPatientId(null);
    }
  }, [localDeletionStatus, onDeletionChange]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("description", {
        header: "Name",
        cell: info => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("patient_id", {
        header: "Patient ID",
        cell: row => {
          const value = row.getValue() || "-";
          return (
            <button
              className={`text-ktx-purple underline hover:text-purple-700 cursor-pointer ${selectedPatientId === value ? 'font-bold' : ''}`}
              onClick={() => onPatientIdClick && onPatientIdClick(value)}
              type="button"
            >
              {value}
            </button>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor("mrn", {
        header: "MRN",
        cell: info => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("doctor_name", {
        header: "Doctor",
        cell: info => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("appointment", {
        header: "Appointment",
        cell: row =>
          row.row.original.date_of_appt && row.row.original.appt_time
            ? `${formatDateMMDDYYYY(row.row.original.date_of_appt)} ${formatTimeTo12Hour(row.row.original.appt_time)}`
            : row.row.original.date_of_appt
              ? formatDateMMDDYYYY(row.row.original.date_of_appt)
              : row.row.original.appt_time
                ? formatTimeTo12Hour(row.row.original.appt_time)
                : "-",
        enableSorting: true,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: info => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("ehr_upload_status", {
        header: "EHR Upload Status",
        cell: info => info.getValue() || "-",
        enableSorting: true,
      }),
      ...(showDeleteColumn
        ? [
            columnHelper.display({
              id: "markDelete",
              header: "Delete",
              cell: info => {
                const patient = info.row.original;
                const isDeleted = localDeletionStatus[patient.patient_id] ?? Boolean(patient.is_deleted);
                const isUpdating = updatingPatientId === patient.patient_id;
                const buttonText = isDeleted ? "Undo Mark Delete" : "Mark Delete";
                return (
                  <button
                    type="button"
                    className={`text-xs font-semibold px-3 py-1 rounded ${
                      isDeleted
                        ? "bg-red-100 text-red-700 hover:bg-red-200"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                    } ${isUpdating ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={() => handleToggleDelete(patient)}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Updating..." : buttonText}
                  </button>
                );
              },
            }),
          ]
        : []),
    ],
    [handleToggleDelete, localDeletionStatus, onPatientIdClick, selectedPatientId, showDeleteColumn, updatingPatientId]
  );

  const [page, setPage] = useState(0);
  const PATIENTS_PER_PAGE = 10;
  const [sorting, setSorting] = useState([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Reset page if data changes
  useEffect(() => { setPage(0); }, [data]);

  // Get sorted rows and paginate them
  const sortedRows = table.getSortedRowModel().rows;
  const pagedRows = paginated ? sortedRows.slice(page * PATIENTS_PER_PAGE, (page + 1) * PATIENTS_PER_PAGE) : sortedRows;

  if (!data.length) return null;

  return (
    <div className="mb-6 overflow-x-auto">
      <table className="min-w-full border border-ktx-purple bg-purple-50 rounded-lg">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className="px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b border-ktx-purple cursor-pointer select-none"
                  onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <span className="ml-1">
                      {header.column.getIsSorted() === false && <span className="text-gray-400">⇅</span>}
                      {header.column.getIsSorted() === "asc" && <span className="text-gray-700">↑</span>}
                      {header.column.getIsSorted() === "desc" && <span className="text-gray-700">↓</span>}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {pagedRows.map(row => (
            <tr
              key={row.id}
              className={selectedPatientId && row.original.patient_id === selectedPatientId ? 'bg-purple-200' : ''}
            >
              {row.getVisibleCells().map(cell => (
                <td
                  key={cell.id}
                  className="px-4 py-2 text-sm text-gray-900 border-b border-ktx-purple"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {paginated && (
        <div className="flex justify-end items-center gap-2 mt-2">
          <button
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.ceil(sortedRows.length / PATIENTS_PER_PAGE)}
          </span>
          <button
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs"
            onClick={() => setPage(p => Math.min(Math.ceil(sortedRows.length / PATIENTS_PER_PAGE) - 1, p + 1))}
            disabled={page >= Math.ceil(sortedRows.length / PATIENTS_PER_PAGE) - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PatientSummaryTable; 
